import { useMemo, useCallback } from 'react'
import type { PropsWithChildren, FunctionComponent } from 'react'
import { spacing, fontWeight, fontSize, colors } from '@retailer-platform/shared-components'
import styled from '@emotion/styled'
import {
  useQualityScorecard,
  isGoodLoadingTime,
  isGoodReliability,
} from '@retailer-platform/domain-quality-scorecards'
import { Text } from '@instacart/tds'
import clientEnv from '../../utils/global/clientEnv'
import { errors } from '../../utils/error-handling/errors'
import { Permission } from '../../legacy/common/types'
import { useCurrentAccountContext } from '../../utils/current-account/CurrentAccountContext'
import { useTotem } from '../../api/totem/useTotem.hooks'
import { useDashMessages } from '../../intl/intl.hooks'
import { useAdminControls } from '../../utils/contexts/admin-controls/AdminControlsContext'
import { useNavContext } from '../../gin-and-tonic/containers/nav/utils/NavContext'
import { useNavV3Gate } from '../../gin-and-tonic/containers/nav-v3/useNavV3Gate'
import { updateTotemSystem } from '../../utils/events/analytics'
import { useIppToolName, useTotemInfo } from './useTotemInfo'

const SLACK_CHANNEL_TYPE_PUBLIC = 'SLACK_CHANNEL_TYPE_PUBLIC'

const HeaderContainer = styled.div({
  display: 'flex',
  width: '100%',
  height: spacing.X32,
  minHeight: spacing.X32,
  paddingLeft: spacing.X16,
  alignItems: 'center',
  fontSize: fontSize.X12,
  borderBottom: `1px solid ${colors.GRAYSCALE.X20}`,
})
const ContactSpan = styled.span({
  marginRight: spacing.X16,
  b: {
    fontWeight: fontWeight.BOLD,
  },
  a: {
    textDecoration: 'underline',
  },
})

const QualityMetrics = styled.div({
  display: 'flex',
  gap: spacing.X16,
  marginRight: spacing.X16,
})

const MetricLink = styled.a({
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'underline',
  },
})

const MetricItem = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: spacing.X4,
})
const MetricLabel = styled.span({
  fontWeight: fontWeight.BOLD,
})

export const ContactHeader: FunctionComponent<PropsWithChildren<unknown>> = () => {
  const i18n = useDashMessages({
    tooltipConfigurationMessage: 'app.admin.contactHeader.tooltip.configuration',
    tooltipHelpMessage: 'app.admin.contactHeader.tooltip.help',
    team: 'app.admin.contactHeader.team',
    slack: 'app.admin.contactHeader.slack',
    oncall: 'app.admin.contactHeader.oncall',
    tool: 'app.admin.contactHeader.tool',
    speed: 'app.admin.contactHeader.speed',
    reliability: 'app.admin.contactHeader.reliability',
    navFeedback: 'app.admin.contactHeader.navFeedback',
  })
  const { account } = useCurrentAccountContext()
  const { showContactHeader } = useAdminControls()
  // Pages that hide the navigation (useHideNavigation) go full-screen — the
  // contact header is part of the chrome, so it hides along with the nav bars.
  const navContext = useNavContext()
  const { enabled: navV3Enabled } = useNavV3Gate()
  const totemInfo = useTotemInfo()
  const { ippToolName } = useIppToolName()
  const qualityScorecard = useQualityScorecard(totemInfo?.routePattern)

  const hasFullTotemViewAccess = useMemo(
    () => account?.permissions.some(p => p.slug === Permission.Totem),
    [account]
  )

  const baseTotemUrl = clientEnv.PUBLIC_CLIENT_TOTEM_URL

  const renderQualityMetricLink = useCallback(
    (value: number, unit: string, isGoodFn: (value: number) => boolean) => (
      <MetricLink
        href="https://dashboard.instacart.com/app/admin/quality-scorecards/overview"
        target="_blank"
        rel="noopener noreferrer"
      >
        <Text
          color={isGoodFn(value) ? 'systemSuccessRegular' : 'systemDetrimentalRegular'}
          typography="accentEmphasized"
          css={{ textDecoration: 'underline' }}
        >
          {value.toFixed(1)}
          {unit}
        </Text>
      </MetricLink>
    ),
    []
  )

  const { data, loading, error } = useTotem({
    // having no externalId will skip the query
    externalId: totemInfo?.routePattern,
    hasFullTotemViewAccess,
  })

  updateTotemSystem(data)

  if (
    navContext?.isNavVisible === false ||
    !showContactHeader ||
    !hasFullTotemViewAccess ||
    !data ||
    loading ||
    error
  ) {
    if (error) {
      errors.captureException(error, `ContactHeader with error + ${error}`)
    }
    return <></>
  }

  // find team name associated with the system. it will exist
  const teamName = data?.owner_team?.workday_org_name

  // find the first public slack channels associated with the system. it may not exist
  const slackChannels = data?.contact_details?.general_slack_channels?.channels?.filter(
    channel => channel.type === SLACK_CHANNEL_TYPE_PUBLIC
  )
  const mainSlackChannel = slackChannels && slackChannels.length > 0 && slackChannels[0]

  // find any on-call handle associated with the system. it may not exist
  const onCallHandle = data?.contact_details?.oncall_slack_group?.handle

  return (
    <HeaderContainer>
      {mainSlackChannel && (
        <ContactSpan>
          <b>{i18n.slack}: </b>
          <a
            href={`https://instacart.enterprise.slack.com/archives/${mainSlackChannel.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            #{mainSlackChannel.handle}
          </a>
        </ContactSpan>
      )}
      {onCallHandle && (
        <>
          <ContactSpan>
            <b>{i18n.oncall}: </b>@{onCallHandle}
          </ContactSpan>
        </>
      )}
      {teamName && (
        <>
          <ContactSpan>
            <b>{i18n.team}: </b>
            <a
              href={`${baseTotemUrl}/teams/${data.owner_team.name}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {teamName}
            </a>
          </ContactSpan>
        </>
      )}
      {ippToolName && (
        <>
          <ContactSpan>
            <b>{i18n.tool}: </b>
            <a
              href={`https://totem.instacart.tools/entities/ipp-tool/${totemInfo?.routePattern}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {ippToolName}
            </a>
          </ContactSpan>
        </>
      )}
      {qualityScorecard && (
        <QualityMetrics>
          {qualityScorecard.p90LoadingTime != null && (
            <MetricItem>
              <MetricLabel>{i18n.speed}:</MetricLabel>
              {renderQualityMetricLink(qualityScorecard.p90LoadingTime, 'sec', isGoodLoadingTime)}
            </MetricItem>
          )}
          {qualityScorecard.viewsWithErrorsPercentage != null && (
            <MetricItem>
              <MetricLabel>{i18n.reliability}:</MetricLabel>
              {renderQualityMetricLink(
                qualityScorecard.viewsWithErrorsPercentage,
                '%',
                isGoodReliability
              )}
            </MetricItem>
          )}
        </QualityMetrics>
      )}
      {navV3Enabled && (
        <ContactSpan>
          <b>{i18n.navFeedback}: </b>
          <a
            href="https://instacart.enterprise.slack.com/archives/C06917XU8TF"
            target="_blank"
            rel="noopener noreferrer"
          >
            #prj-ipp-ia
          </a>
        </ContactSpan>
      )}
    </HeaderContainer>
  )
}
